import {
  useImperativeHandle,
  type PropsWithChildren,
  type Ref,
  type RefObject,
} from 'react';

import { useGridPlotSize } from '../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { INTERACTION_MODE } from '../../core/components/interactions-overlay/types.js';
import { reduceInteractionMode } from '../../core/components/interactions-state-helpers/reducers/mode-reducers.js';
import { useTriggerZoom } from '../../core/components/zoom/hooks/useTriggerZoom.js';
import { ChartType } from '../../core/types/chart-types.js';
import { ZOOM_CHANGE_TYPE } from '../../core/types/zoom.js';
import { useApplyZoomDelta } from '../components/overlay/hooks/useApplyZoomDelta.js';
import { useResetInteractions } from '../components/overlay/hooks/useResetInteractions.js';
import { useXYChartData } from '../hooks/useXYChartData.js';
import { useSetState, useZoomState } from '../store/selectors.js';
import type { XYChartConfigProperties, XYChartRef } from '../types/xy-chart.js';
import { buildAndDownloadCsv } from '../utils/build-and-download-csv.js';

interface XYChartImperativeHandlerBuilderProps {
  chartRef: Ref<XYChartRef>;
  containerRef: RefObject<HTMLDivElement | null>;
  config: XYChartConfigProperties;
}

export const XYChartImperativeHandlerBuilder = ({
  chartRef,
  containerRef,
  config,
  children,
}: PropsWithChildren<XYChartImperativeHandlerBuilderProps>) => {
  const { seriesWithDatapoints } = useXYChartData();
  const setState = useSetState();
  const { zoomInEnabled, zoomOutEnabled } = useZoomState();
  const { axesMetadata } = useXYChartData();

  const { width } = useGridPlotSize();
  const applyZoom = useApplyZoomDelta();
  const triggerZoom = useTriggerZoom(width, applyZoom);

  const resetInteractions = useResetInteractions();

  useImperativeHandle(chartRef, () => ({
    element: containerRef.current,
    getConfig: () =>
      JSON.stringify({
        type: ChartType.XYChart,
        ...config,
      }),
    downloadData: () => {
      buildAndDownloadCsv(seriesWithDatapoints);
    },
    exploreMode: () => {
      setState(reduceInteractionMode(INTERACTION_MODE.Explore));
    },
    zoomMode: () => {
      if (axesMetadata.isZoomEnabled) {
        setState(reduceInteractionMode(INTERACTION_MODE.Zoom));
      }
    },
    panMode: () => {
      if (zoomOutEnabled && axesMetadata.isPanEnabled) {
        setState(reduceInteractionMode(INTERACTION_MODE.Pan));
      }
    },
    zoomIn: () => {
      if (zoomInEnabled) {
        triggerZoom(ZOOM_CHANGE_TYPE.ZoomIn);
      }
    },
    zoomOut: () => {
      if (zoomOutEnabled) {
        triggerZoom(ZOOM_CHANGE_TYPE.ZoomOut);
      }
    },
    reset: () => {
      resetInteractions();
    },
  }));

  return children;
};
